<script setup lang="ts">
defineProps<{
  side?: boolean
  section?: 'account' | 'utilities'
  themeTitle: string
  themeIconClass: string
  showRandomContentButton: boolean
  randomContentLoading: boolean
  showLoginButton: boolean
  isAuthenticated: boolean
  authUserName: string
  authUserUUID?: string
  loginLoading: boolean
  logoutLoading: boolean
  authMenuOpen: boolean
  authErrorMessage: string
  authPopupTop: number
}>()

defineEmits<{
  search: []
  theme: []
  random: []
  auth: [event: MouseEvent]
  logout: []
}>()
</script>

<template>
  <div class="nav-actions" :class="{ 'nav-actions-side': side }" :style="{ '--auth-popup-top': `${authPopupTop}px` }">
    <div v-if="section !== 'account'" class="utility-actions">
      <button v-if="side" class="search-icon-btn" type="button" title="搜索" aria-label="搜索" @click="$emit('search')">
        <i class="fa-solid fa-magnifying-glass" aria-hidden="true" />
      </button>
      <button class="theme-icon-btn" type="button" :title="themeTitle" :aria-label="themeTitle" @click="$emit('theme')">
        <i :class="themeIconClass" aria-hidden="true" />
      </button>
      <button
        v-if="showRandomContentButton"
        class="random-icon-btn"
        type="button"
        title="随机文章"
        aria-label="随机文章"
        :disabled="randomContentLoading"
        @click="$emit('random')"
      >
        <i class="fa-solid fa-shuffle" aria-hidden="true" />
      </button>
    </div>
    <div v-if="showLoginButton && section !== 'utilities'" class="auth-area">
      <button
        class="auth-icon-btn"
        type="button"
        :title="isAuthenticated ? `当前用户：${authUserName}` : '登录'"
        :aria-label="isAuthenticated ? `当前用户：${authUserName}` : '登录'"
        :disabled="loginLoading"
        @click="$emit('auth', $event)"
      >
        <BlogNavItemLabel
          v-if="side"
          side
          :name="isAuthenticated ? authUserName : '登录'"
          :icon="isAuthenticated ? 'fa-solid fa-user' : 'fa-solid fa-right-to-bracket'"
        />
        <i v-else :class="isAuthenticated ? 'fa-solid fa-user' : 'fa-solid fa-right-to-bracket'" aria-hidden="true" />
      </button>
      <Transition name="auth-pop-fade">
        <div v-if="isAuthenticated && authMenuOpen" class="auth-pop">
          <p class="auth-user-name">
            <i class="fa-solid fa-user" aria-hidden="true" />
            <span>{{ authUserName }}</span>
          </p>
          <p v-if="authUserUUID" class="auth-user-uuid">UUID: {{ authUserUUID }}</p>
          <button type="button" class="auth-logout-btn" :disabled="logoutLoading" @click="$emit('logout')">
            <i class="fa-solid fa-arrow-right-from-bracket" aria-hidden="true" />
            <span>{{ logoutLoading ? '退出中...' : '退出登录' }}</span>
          </button>
        </div>
      </Transition>
      <p v-if="authErrorMessage" class="auth-error-notice">{{ authErrorMessage }}</p>
    </div>
  </div>
</template>

<style scoped>
.nav-actions,
.utility-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.auth-area {
  position: relative;
  min-width: 0;
}

.search-icon-btn,
.theme-icon-btn,
.random-icon-btn,
.auth-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  color: var(--text);
  cursor: pointer;
  background: transparent;
}

.search-icon-btn i,
.theme-icon-btn i,
.random-icon-btn i,
.auth-icon-btn i {
  font-size: 16px;
  line-height: 1;
}

.auth-icon-btn:disabled {
  opacity: 0.65;
  cursor: wait;
}

.auth-pop {
  border: 0;
  border-radius: 12px;
  background: var(--bg-soft);
  box-shadow: var(--floating-shadow);
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 14;
  display: grid;
  gap: 8px;
  min-width: 210px;
  padding: 10px;
  font-weight: 400;
}

.auth-user-name {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-weight: 700;
  color: var(--text);
}

.auth-user-name i {
  color: var(--brand);
}

.auth-user-uuid {
  margin: 0;
  font-size: 12px;
  color: var(--text-soft);
  word-break: break-all;
}

.auth-logout-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 34px;
  border: 0;
  border-radius: 999px;
  color: var(--text);
  background: var(--bg);
  cursor: pointer;
}

.auth-logout-btn:hover:not(:disabled) {
  color: var(--brand);
  background: var(--brand-soft);
}

.auth-logout-btn:disabled {
  opacity: 0.65;
  cursor: wait;
}

.auth-error-notice {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 13;
  width: max-content;
  max-width: min(320px, calc(100vw - 24px));
  margin: 0;
  padding: 7px 10px;
  border: 1px solid color-mix(in srgb, #c03636 24%, var(--line));
  border-radius: 9px;
  color: #c03636;
  font-size: 12px;
  background: color-mix(in srgb, #c03636 8%, var(--bg));
  box-shadow: var(--shadow);
}

.auth-pop-fade-enter-active,
.auth-pop-fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.auth-pop-fade-enter-from,
.auth-pop-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.nav-actions-side {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  min-width: 0;
  flex-shrink: 0;
  gap: 12px;
}

.nav-actions-side .utility-actions {
  justify-content: space-evenly;
}

.nav-actions-side .search-icon-btn,
.nav-actions-side .theme-icon-btn,
.nav-actions-side .random-icon-btn {
  width: 44px;
  height: 44px;
}

.nav-actions-side .search-icon-btn:hover,
.nav-actions-side .theme-icon-btn:hover,
.nav-actions-side .random-icon-btn:hover:not(:disabled) {
  background: var(--editor-bg);
}

.nav-actions-side .auth-icon-btn {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  height: auto;
  min-height: 52px;
  padding: 12px 20px;
  justify-content: flex-start;
  text-align: left;
  color: #fff;
  background: var(--brand);
  font: inherit;
  font-weight: 600;
}

.nav-actions-side .auth-icon-btn:hover:not(:disabled) {
  background: color-mix(in srgb, var(--brand) 90%, var(--text));
}

.nav-actions-side .auth-pop,
.nav-actions-side .auth-error-notice {
  position: fixed;
  top: var(--auth-popup-top);
  left: calc(var(--site-nav-left) + var(--site-nav-width) + 12px);
  right: auto;
}

.nav-actions-side .auth-pop {
  min-width: 0;
  width: 180px;
}

.nav-actions-side .auth-user-name span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
